export type EventType = "corporate_retreat" | "away_day" | "corporate_party" | "team_building" | "conference" | "product_launch" | "other";
export type PriceTier = "budget" | "standard" | "premium";

export interface Services {
  restauration: boolean;
  transport: boolean;
  activities: boolean;
}

export interface BudgetBreakdown {
  venue: number;
  restauration: number;
  transport: number;
  activities: number;
  total: number;
}

const basePrices = {
  venue: { budget: 45, standard: 65, premium: 95 },
  restauration: { budget: 35, standard: 55, premium: 85 },
  transport: { budget: 25, standard: 35, premium: 50 },
  activities: { budget: 30, standard: 50, premium: 80 }
};

const eventTypeMultipliers: Record<EventType, number> = {
  "corporate_retreat": 1.2,
  "away_day": 1.0,
  "corporate_party": 1.1,
  "team_building": 0.9,
  "conference": 1.0,
  "product_launch": 1.3,
  "other": 1.0
};

export const eventTypeLabels: Record<EventType, string> = {
  "corporate_retreat": "Séminaire",
  "away_day": "Journée d'équipe",
  "corporate_party": "Soirée d'entreprise",
  "team_building": "Activités team building",
  "conference": "Conférence",
  "product_launch": "Lancement produit",
  "other": "Autre"
};

export const priceTierLabels: Record<PriceTier, string> = {
  "budget": "Économique",
  "standard": "Standard",
  "premium": "Premium"
};

const getGroupSizeMultiplier = (participants: number): number => {
  if (participants >= 50) return 0.85;
  if (participants >= 30) return 0.9;
  if (participants >= 20) return 0.95;
  return 1.0;
};

export const calculateBudget = (
  eventType: EventType, 
  priceTier: PriceTier, 
  participants: number, 
  days: number, 
  services: Services
): number => {
  const eventMultiplier = eventTypeMultipliers[eventType];
  const groupMultiplier = getGroupSizeMultiplier(participants);
  
  const venueCost = basePrices.venue[priceTier] * participants * days * eventMultiplier * groupMultiplier;
  const restaurationCost = services.restauration ? basePrices.restauration[priceTier] * participants * days * eventMultiplier * groupMultiplier : 0;
  const transportCost = services.transport ? basePrices.transport[priceTier] * participants * eventMultiplier * groupMultiplier : 0;
  const activitiesCost = services.activities ? basePrices.activities[priceTier] * participants * days * eventMultiplier * groupMultiplier : 0;
  
  return Math.round(venueCost + restaurationCost + transportCost + activitiesCost);
};

export const calculateBudgetBreakdown = (
  eventType: EventType, 
  priceTier: PriceTier, 
  participants: number, 
  days: number, 
  services: Services
): BudgetBreakdown => {
  const eventMultiplier = eventTypeMultipliers[eventType];
  const groupMultiplier = getGroupSizeMultiplier(participants);
  
  const venue = Math.round(basePrices.venue[priceTier] * participants * days * eventMultiplier * groupMultiplier);
  const restauration = services.restauration ? Math.round(basePrices.restauration[priceTier] * participants * days * eventMultiplier * groupMultiplier) : 0;
  const transport = services.transport ? Math.round(basePrices.transport[priceTier] * participants * eventMultiplier * groupMultiplier) : 0;
  const activities = services.activities ? Math.round(basePrices.activities[priceTier] * participants * days * eventMultiplier * groupMultiplier) : 0;
  
  return { 
    venue, 
    restauration, 
    transport, 
    activities, 
    total: venue + restauration + transport + activities 
  };
};
